import React, { useState, useRef } from "react";
import { ArrowLeft, RotateCcw, Palette, Download } from "lucide-react";

const PatternDrawingGame = ({ onBack }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState("#4F46E5");
  const [brushSize, setBrushSize] = useState(3);
  const [selectedPattern, setSelectedPattern] = useState("freehand");

  const colors = [
    "#4F46E5",
    "#7C3AED",
    "#EC4899",
    "#EF4444",
    "#F97316",
    "#EAB308",
    "#22C55E",
    "#06B6D4",
    "#6B7280",
    "#1F2937",
  ];

  const patterns = [
    {
      id: "freehand",
      name: "Freehand",
      description: "Draw whatever feels right",
    },
    {
      id: "circles",
      name: "Circles",
      description: "Repetitive circular patterns",
    },
    { id: "waves", name: "Waves", description: "Flowing wave patterns" },
    {
      id: "mandala",
      name: "Mandala",
      description: "Centered circular designs",
    },
    {
      id: "geometric",
      name: "Geometric",
      description: "Triangles and squares",
    },
  ];

  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if (e.touches && e.touches[0]) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getEventPos(e);

    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const pos = getEventPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.strokeStyle = currentColor;
    ctx.fillStyle = currentColor;

    if (selectedPattern === "freehand") {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (selectedPattern === "circles") {
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, brushSize * 2, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (selectedPattern === "waves") {
      ctx.lineTo(pos.x + Math.sin(Date.now() * 0.01) * 10, pos.y);
      ctx.stroke();
    } else if (selectedPattern === "mandala") {
      const canvas = canvasRef.current;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI * 2) / 8;
        const rotatedX =
          centerX +
          (pos.x - centerX) * Math.cos(angle) -
          (pos.y - centerY) * Math.sin(angle);
        const rotatedY =
          centerY +
          (pos.x - centerX) * Math.sin(angle) +
          (pos.y - centerY) * Math.cos(angle);

        ctx.beginPath();
        ctx.arc(rotatedX, rotatedY, brushSize, 0, 2 * Math.PI);
        ctx.fill();
      }
    } else if (selectedPattern === "geometric") {
      const gridSize = 25;
      const snapX = Math.round(pos.x / gridSize) * gridSize;
      const snapY = Math.round(pos.y / gridSize) * gridSize;

      ctx.fillRect(
        snapX - brushSize,
        snapY - brushSize,
        brushSize * 2,
        brushSize * 2
      );
    }
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const downloadDrawing = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "my-pattern-drawing.png";
    link.href = canvas.toDataURL();
    link.click();
  };

  const drawPatternGuide = (patternId) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const currentStrokeStyle = ctx.strokeStyle;
    const currentLineWidth = ctx.lineWidth;

    ctx.strokeStyle = "#E5E7EB";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    switch (patternId) {
      case "mandala":
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI * 2) / 8;
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(
            centerX + Math.cos(angle) * 200,
            centerY + Math.sin(angle) * 200
          );
          ctx.stroke();
        }

        for (let r = 50; r <= 200; r += 50) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, r, 0, 2 * Math.PI);
          ctx.stroke();
        }
        break;

      case "geometric":
        const gridSize = 25;
        ctx.strokeStyle = "#E5E7EB";

        for (let x = 0; x <= canvas.width; x += gridSize) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvas.height);
          ctx.stroke();
        }

        for (let y = 0; y <= canvas.height; y += gridSize) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(canvas.width, y);
          ctx.stroke();
        }
        break;
    }

    ctx.setLineDash([]);
    ctx.strokeStyle = currentStrokeStyle;
    ctx.lineWidth = currentLineWidth;
  };

  const showPatternGuide = () => {
    if (selectedPattern !== "freehand") {
      drawPatternGuide(selectedPattern);
    }
  };

  return (
    <div className="min-h-screen  p-4">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-white/80 rounded-xl hover:bg-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <h1 className="text-lg sm:text-2xl font-bold text-gray-800 text-center flex-1 mx-2">
          <span className="hidden sm:inline">✏️ Pattern Drawing</span>
          <span className="sm:hidden">✏️ Drawing</span>
        </h1>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={downloadDrawing}
            className="bg-green-500 hover:bg-green-600 text-white px-2 sm:px-3 py-2 rounded-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Save</span>
          </button>
          <button
            onClick={clearCanvas}
            className="bg-red-500 hover:bg-red-600 text-white px-2 sm:px-3 py-2 rounded-lg flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
          >
            <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Clear</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto">
        <div className="lg:w-80 space-y-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Drawing Patterns
            </h3>
            <div className="space-y-2">
              {patterns.map((pattern) => (
                <button
                  key={pattern.id}
                  onClick={() => setSelectedPattern(pattern.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedPattern === pattern.id
                      ? "bg-indigo-100 border-2 border-indigo-300"
                      : "bg-white/50 hover:bg-white/70 border-2 border-transparent"
                  }`}
                >
                  <div className="font-medium">{pattern.name}</div>
                  <div className="text-sm text-gray-600">
                    {pattern.description}
                  </div>
                </button>
              ))}
            </div>

            {selectedPattern !== "freehand" && (
              <button
                onClick={showPatternGuide}
                className="w-full mt-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg transition-colors"
              >
                Show {patterns.find((p) => p.id === selectedPattern)?.name}{" "}
                Guide
              </button>
            )}
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Colors</h3>
            <div className="grid grid-cols-5 gap-2">
              {colors.map((color) => (
                <button
                  key={color}
                  onClick={() => setCurrentColor(color)}
                  className={`w-10 h-10 rounded-full transition-transform ${
                    currentColor === color
                      ? "scale-110 ring-4 ring-gray-300"
                      : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">Brush Size</h3>
            <input
              type="range"
              min="1"
              max="20"
              value={brushSize}
              onChange={(e) => setBrushSize(e.target.value)}
              className="w-full"
            />
            <div className="text-center mt-2">
              <div
                className="mx-auto rounded-full"
                style={{
                  width: `${brushSize * 2}px`,
                  height: `${brushSize * 2}px`,
                  backgroundColor: currentColor,
                }}
              />
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4">
            <h3 className="font-semibold mb-3">How to Use:</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Choose a pattern type to guide your drawing</li>
              <li>• Select colors that feel calming to you</li>
              <li>• Focus on repetitive motions to relax</li>
              <li>• Let your creativity flow naturally</li>
              <li>• Save your artwork when finished</li>
            </ul>
          </div>
        </div>

        <div className="flex-1">
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <canvas
              ref={canvasRef}
              width={800}
              height={600}
              className="border border-gray-200 rounded-lg cursor-crosshair w-full touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternDrawingGame;
